import { FC } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import { Box, Container, Fade, Paper, styled, Typography } from '@mui/material';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import usePayNowDetailsToken from '@acorn-portal/shared/src/hooks/use-paynow-details-token';
import MissingPaymentForm from '@acorn-portal/shared/src/components/forms/MissingPaymentForm';
import { PayNowTokenisedLinkError } from '@acorn-portal/shared/src/components/payNow';
import { useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { SplitPaymentStatus } from '@acorn-portal/shared/src/services/api';
import { IMissingPaymentFormValues } from '@acorn-portal/shared/src/hooks/useMissingPaymentForm';

const ImportCopyText = styled(Typography)(({ theme }) => ({
	fontWeight: 'bold',
	textAlign: 'left',
	margin: theme.spacing(2, 0),
}));

const TokenisedPaymentAgreement: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const searchParams = new URLSearchParams(location.search);

	let token = searchParams.get('token') ?? '';
	token = token.split('#')[0];

	const { payNowDetails, messageComponent, messageSeverity, loading } = usePayNowDetailsToken(token);
	const { details, collectionOptions } = payNowDetails;

	const handleSubmit = (values: IMissingPaymentFormValues) => {
		const overDuePaymentPlan = collectionOptions.find((x) => x.paymentPlanType === values.splitPaymentPlanType);

		if (!overDuePaymentPlan) {
			navigate(ROUTE_PATHS.error);
			return;
		}

		const selectedPlan = overDuePaymentPlan.installments.find((x) => x.installmentNumber === 1);

		if (!selectedPlan) {
			navigate(ROUTE_PATHS.error);
			return;
		}
		dispatch(
			updatePayNowDetails({
				details: {
					...details!,
					email: values.email,
					overDuePaymentPlan: {
						dateOfPayment: selectedPlan.dueDate!,
						amount: selectedPlan.amount!,
						splitPaymentId: '',
						status: SplitPaymentStatus.Pending,
						isConsolidated: false,
					},
				},
				collectionOptions,
				missingPaymentFormDetails: values,
			}),
		);

		navigate({
			pathname: ROUTE_PATHS.payNowDirectPayment,
			search: `?${createSearchParams({
				token,
				isInitialPayment: '1',
			})}`,
		});
	};

	return (
		<FullPageLoadingSplash loading={loading} message='Retrieving Payment Details...'>
			<Paper
				elevation={1}
				sx={{
					margin: 1,
					borderRadius: '8px',
					paddingBottom: 3,
				}}
			>
				<Fade in>
					<Container maxWidth='md' sx={{ padding: 4 }}>
						{messageSeverity && messageComponent ? (
							<PayNowTokenisedLinkError severity={messageSeverity} message='There is an issue with your payment link'>
								{messageComponent}
							</PayNowTokenisedLinkError>
						) : (
							<Box>
								<PageTitle>Outstanding Payment</PageTitle>
								<Box sx={{ m: 2 }} />
								<ImportCopyText>
									Here are the details of the payment which is outstanding with us. Please select one of the payment
									options below to continue your cover with us.
								</ImportCopyText>
								<Box sx={{ m: 2 }} />
								<MissingPaymentForm
									details={details!}
									paymentCollection={collectionOptions}
									scheme={undefined}
									onSubmit={handleSubmit}
								/>
							</Box>
						)}
					</Container>
				</Fade>
			</Paper>
		</FullPageLoadingSplash>
	);
};

export default TokenisedPaymentAgreement;
